/**
 * isiXhosa: Billing
 *
 * What this file covers: Credits, plans, top-ups, the ledger, usage, alerts and invoices.
 *
 * Tone: Exact about money. Credits are what is spent; R is used only for rand actually paid. Never use words
 * like margin, markup or profit. Write amounts and dates the way the language writes them.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const billing: Catalog = {
  'billing.currency': 'Imali',
  'billing.fullLedger': 'Incwadi epheleleyo →',
  'billing.ledger.columns.when': 'Nini',
  'billing.ledger.loadError': 'Ayikwazanga ukulayisha incwadi yeengxelo.',
  'billing.ledger.title': 'Incwadi yeengxelo',
  'billing.lowBalance': 'Ibhalansi ephantsi',
  'billing.emptyBalance': 'Akukho tyala',
  'billing.noTransactionsYet': 'Akukho ntengiselwano okwangoku.',
  'billing.recentTransactions': 'Iintengiselwano zakutshanje',
  'billing.topUp': 'Gcwalisa →',
  'billing.topUpNow': 'Gcwalisa ngoku →',
  'billing.topup.pay': 'Bhatala',
  'billing.topup.paymentMethod': 'Indlela yokubhatala',
  'billing.topup.topUp': 'Ukugcwalisa',
  'billing.walletBalance': 'Ibhalansi yesipaji',
  'billing.alerts.low.emailPlaceholder': 'finance@example.com, owner@example.com',
  'billing.alerts.low.msisdnPlaceholder': '+27 82 000 1234',
  'billing.category.meta': 'I-Messenger ne-Instagram',
  'billing.ledger.refundedTotal': 'Kubuyisiwe',
  'billing.ledger.planFeesTotal': 'Iintlawulo zesicwangciso',
  'billing.ledger.spentHint': 'Imiyalezo, i-AI nogcino',
  'billing.ledger.planFeesHint': 'Ubhaliso lwakho, kuthatyathwe nayiphi na ikhredithi yokutshintsha isicwangciso',
  'billing.ledger.addedHint': 'Ukongeza imali, izibonelelo neekhredithi ezibandakanywe kwisicwangciso sakho',
  'billing.ledger.totalsNote': 'Okongeziweyo nokubuyisiweyo, kuthatyathwe okusetyenzisiweyo neentlawulo zesicwangciso, yibhalansi yakho xa kungekho sihluzi.',
  'billing.ledger.exportFailed': 'Ileja ayikwazanga ukukhutshwa. Zama kwakhona.',
  'billing.ledger.exporting': 'Iyakhupha…',
  'billing.plan.limits.title': 'Okuvumelekileyo',
  'billing.plan.limit.bots': 'Ii-chatbot',
  'billing.plan.limit.numbers': 'Iinombolo ze-WhatsApp',
  'billing.plan.limit.team_members': 'Amalungu eqela',
  'billing.plan.limit.flows': 'Ukuhamba kwe-chatbot',
  'billing.plan.limit.knowledge_documents': 'Amaxwebhu esiseko solwazi',
  'billing.plan.limit.unlimited': 'Akukho mda',
  'billing.wallet.bought': 'Okuthengiweyo: {{amount}}',
  'billing.wallet.fromPlan': 'Okuvela kwisicwangciso sakho: {{amount}}',
  'billing.wallet.split.info': 'Iikhredithi ezivela kwisicwangciso sakho zisetyenziswa kuqala kwaye zezemiyalezo. Intlawulo yenyanga yesicwangciso sakho ihlawulwa ngeekhredithi ozithengileyo.',
  'billing.wallet.split.infoLabel': 'Malunga neekhredithi ezithengiweyo nezo zivela kwisicwangciso sakho',
  'billing.plan.prices.title': 'Amaxabiso emiyalezo, ngeekhredithi',
  'billing.plan.prices.marketing': 'Umyalezo wentengiso',
  'billing.plan.prices.utility': 'Umyalezo wesaziso',
  'billing.plan.prices.service': 'Impendulo kwiiyure ezingama-24',
  'billing.plan.prices.cheaper': 'Amaxabiso emiyalezo aphantsi kunalawo uwahlawulayo ngoku',
  'billing.plan.refused.title': 'Iikhredithi ezithengiweyo azonelanga kwi-{{name}}',
  'billing.plan.refused.body': 'Intlawulo yesicwangciso ihlawulwa ngeekhredithi ozithengileyo. Iikhredithi ezithengiweyo eziseleyo: {{bought}}. Okufunekayo: {{required}}. Okuvela kwisicwangciso sakho ({{included}}) kunokusetyenziswa kwimiyalezo kuphela.',
  'billing.plan.refused.next': 'Gcwalisa {{shortfall}} okanye ngaphezulu, uze ukhethe i-{{name}} kwakhona.',
  'billing.usage.rates.view': 'Amaxabiso aza kuboniswa',
  'billing.usage.rates.view.mine': 'Amaxabiso akho',
  'billing.usage.rates.view.compare': 'Thelekisa izicwangciso',
  'billing.usage.rates.changePlan': 'Tshintsha isicwangciso →',
  'billing.usage.rates.compareHint': 'Oko kubizwa yiyunithi enye kwisicwangciso ngasinye, ngeekhredithi. Isicwangciso sakho siphawulwe.',
  'billing.usage.rates.yourPlan': 'Isicwangciso sakho',
  'billing.usage.rates.yourPlanPrice': 'Ixabiso lakho le-{{plan}}',
  'billing.usage.rates.payg': 'Ixabiso lokuhlawula njengoko usebenzisa:',
  'billing.usage.rates.planNote': 'Ukwisicwangciso i-{{plan}}. Apho ixabiso lakho liphantsi kunexabiso lokuhlawula njengoko usebenzisa, elo xabiso liboniswa likrwelelwe umgca ecaleni kwelakho.',
  'billing.explain.planPrice': 'Kubizwe ngexabiso lakho le-{{plan}} (ukuhlawula njengoko usebenzisa yi-{{list}})',
  'billing.explain.planPriceUnnamed': 'Kubizwe ngexabiso lesicwangciso sakho (ukuhlawula njengoko usebenzisa yi-{{list}})',
  'billing.plan.modalBody': 'Intlawulo yenyanga ihlawulwa ngeekhredithi ozithengileyo. Iikhredithi ezibandakanywe kwisicwangciso zongezwa kwisipaji sakho ngalo lonke ixesha kuhlawulwa, kwaye zisetyenziswa kuqala, kwimiyalezo. Okukhona isicwangciso siphezulu, kokukhona umyalezo ngamnye ubiza kancinci.',
  'billing.topup.providerDisabled': 'Loo ndlela yokuhlawula isandul ukucinywa. Khetha enye, okanye uzame kwakhona emva kwexesha.',
  'billing.alerts.methods.unavailable.title': 'Ukugcina ikhadi akufumaneki okwangoku',
  'billing.alerts.methods.unavailable.body': 'Amakhadi osele uwagcinile ahlala apha, kodwa ukuzalisa okuzenzekelayo kumisiwe de kuvulwe kwakhona ukuhlawula ngekhadi. Usenako ukuzalisa ngesandla.',
  'billing.topup.result.checking.title': 'Siqinisekisa intlawulo yakho',
  'billing.topup.result.checking.body': 'Silinde u-{{provider}} ukuba ayiqinisekise. Oku kudla nje imizuzwana embalwa.',
  'billing.topup.result.checking.bodyPlain': 'Oku kudla nje imizuzwana embalwa.',
  'billing.topup.result.slow.title': 'Sisaqinisekisa',
  'billing.topup.result.slow.body': 'U-{{provider}} akakayiqinisekisi intlawulo yakho. Iikhredithi zakho ziza kuvela xa sele eyiqinisekisile, ngoko ungakuvala oku.',
  'billing.topup.result.success.title': 'Intlawulo iphumelele',
  'billing.topup.result.success.body': 'Kongezwe {{credits}} kwisipaji sakho.',
  'billing.topup.result.success.receipt': 'Irisithi isendleleni eya kwi-imeyile yakho.',
  'billing.topup.result.failed.title': 'Intlawulo ayigqitywanga',
  'billing.topup.result.failed.body': 'U-{{provider}} akayigqibanga le ntlawulo, ngoko akukho zikhredithi zongeziweyo. Ukuba imali iphumile kwiakhawunti yakho, qhagamshelana nathi usebenzisa isalathiso esingezantsi.',
  'billing.topup.result.paid': 'Kuhlawulwe',
  'billing.topup.result.added': 'Iikhredithi ezongeziweyo',
  'billing.topup.result.balance': 'Ibhalansi ngoku',
  'billing.topup.result.reference': 'Isalathiso {{reference}}',
  'billing.topup.result.tryAgain': 'Zama kwakhona',
  'billing.topup.result.done': 'Kulungile'
};

export default billing;
